// START IMPORTS
import { CookiesStorage } from '@ltvco/refresh-lib';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Text, Grid, Link, IconButton, Stack } from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  FirstRecordSearch,
  getFirstRecordSearch,
} from '@ltvco/refresh-lib/utils';
import { useSnackbar, closeSnackbar, useAccount } from '@ltvco/refresh-lib/v1';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { SidenavContainer } from '../components/sidenav/SidenavContainer';
// END IMPORTS

export function DashboardParent() {
  // START HOOKS_AND_QUERIES
  const { enqueueSnackbar } = useSnackbar();
  const { routingUtils } = useContext(AppConfig);
  const navigate = useNavigate();
  const { data: account } = useAccount(true);
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const cookieStorage = new CookiesStorage();
  const dateUtil = new DateUtil();
  const currentDate = new Date();
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    const firstRecordSearchData = getFirstRecordSearch();

    if (firstRecordSearchData && 'searchType' in firstRecordSearchData) {
      const typesForReportRoute = ['phone', 'email', 'username', 'property'];
      if (typesForReportRoute.includes(firstRecordSearchData.searchType)) {
        handleRedirectToReportRoute(firstRecordSearchData as FirstRecordSearch);
      }

      const typesForSearchRoute = [
        'person',
        'for_sale',
        'pre_foreclosures',
        'unclaimed_money',
      ];
      if (typesForSearchRoute.includes(firstRecordSearchData.searchType)) {
        handleRedirectToSearchRoute(firstRecordSearchData);
      }

      cookieStorage.removeValue('first_record_search');
    }
  });

  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);
  // END EFFECTS

  // START HELPER_FUNCTIONS
  function handleRedirectToSearchRoute(
    firstRecordSearchData: FirstRecordSearch
  ) {
    if (
      firstRecordSearchData.last_name &&
      (firstRecordSearchData.searchType === 'person' ||
        firstRecordSearchData.searchType === 'unclaimed_money')
    ) {
      navigate(
        routingUtils.searchPersonUrl({
          fName: firstRecordSearchData.first_name,
          mName: firstRecordSearchData.middle_name,
          lName: firstRecordSearchData.last_name,
          state: firstRecordSearchData.state,
          city: firstRecordSearchData.city,
          age: firstRecordSearchData.age,
        })
      );
    } else if (
      firstRecordSearchData.searchType === 'for_sale' ||
      firstRecordSearchData.searchType === 'pre_foreclosures'
    ) {
      navigate(
        routingUtils.searchPropertyListingsUrl({
          city: firstRecordSearchData.city,
          state: firstRecordSearchData.state,
          zip5: firstRecordSearchData.zip_code,
          search_type:
            firstRecordSearchData?.searchType === 'pre_foreclosures'
              ? 'pre_foreclosures'
              : undefined,
        })
      );
    }
  }

  function handleRedirectToReportRoute(
    firstRecordSearchData: FirstRecordSearch
  ) {
    let queryParams = '';

    if (firstRecordSearchData.searchType === 'phone') {
      queryParams = `phone=${firstRecordSearchData.phone_number}`;
    }
    if (firstRecordSearchData.searchType === 'email') {
      queryParams = `email=${firstRecordSearchData.email}`;
    }
    if (firstRecordSearchData.searchType === 'username') {
      queryParams = `username=${firstRecordSearchData.username}`;
    }
    if (firstRecordSearchData.searchType === 'property') {
      queryParams = `address=${firstRecordSearchData.address}&city=${firstRecordSearchData.city}&state=${firstRecordSearchData.state}&zipcode=${firstRecordSearchData.zip_code}`;
    }

    const navigateTo = `/report/${firstRecordSearchData.searchType}?${queryParams}`;
    navigate(navigateTo);
    // Setting this cookie for redirecting properly on the Onboarding Questions
    // cookieStorage.setValue('first_report_navigate', navigateTo);
  }
  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);

  function handleReactivationForPausedAccountsBanner() {
    if (!account) return;

    const isInPausedStatus = account?.meta?.subscription_state === 'paused';
    if (!isInPausedStatus) return;

    const today = currentDate.toISOString();
    const expirationDate = account?.account.subscription_info?.normalize_date;

    if (!expirationDate) return;

    const isAfterCurrentDate = dateUtil.isAfter(today, expirationDate);

    const expiredMessage = (
      <Text>
        <b>
          Your account expired on{' '}
          {dateUtil.parseDateFromString(
            account?.account.subscription_info?.normalize_date,
            'M/d/y'
          )}
          . Reactivate to regain access.
        </b>
      </Text>
    );

    const cancelledMessage = (
      <Text>
        <b>
          Your account with member ID {account?.account.user_info.user_code} has
          been cancelled.
        </b>{' '}
        <br /> You have until{' '}
        {dateUtil.parseDateFromString(
          account?.account.subscription_info?.normalize_date,
          'M/d/y'
        )}{' '}
        to continue running reports. Please reactivate now to maintain access.
        Visit{' '}
        <Link target="_blank" href="https://numberguru.com/rf/reactivation">
          here
        </Link>{' '}
        to reactivate.
      </Text>
    );

    const message = isAfterCurrentDate ? expiredMessage : cancelledMessage;

    const pausedAccountSnackbarId = enqueueSnackbar(
      <Stack direction="row" justifyContent="space-between">
        {message}
        {getCloseButton(() => {
          closeSnackbar(pausedAccountSnackbarId);
        })}
      </Stack>,
      {
        variant: 'info',
        persist: true,
        action: <></>,
      }
    );

    localStorage.setItem(
      'pausedAccountSnackbarId',
      JSON.stringify(pausedAccountSnackbarId)
    );
  }

  function getCloseButton(onClick: () => void) {
    return (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    );
  }

  function handleSiteNotifications() {
    if (!account) return;
    const siteNotification = account?.meta?.notification;
    if (!siteNotification) return;
    const siteNotificationSnackbarId = enqueueSnackbar(
      <Text>
        <b>{siteNotification.title}</b> <br /> {siteNotification.message}
      </Text>,
      {
        variant: 'info',
        persist: true,
        action: (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              closeSnackbar(siteNotificationSnackbarId);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  }
  // END HELPER_FUNCTIONS

  // START RENDER
  return (
    <SidenavContainer>
      <Grid container justifyContent={'space-around'}>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
          maxWidth={{
            xs: '100%',
            md: '100%',
          }}
        >
          <Outlet />
        </Grid>
      </Grid>
    </SidenavContainer>
  );
  // END RENDER
}
